import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { FiHeart, FiShoppingCart } from 'react-icons/fi'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import {
  formatProductPrice,
  getProductPrimaryImage,
} from '../../api/productApi'
import { useCart } from '../../context/CartContext'
import { useWishlist } from '../../context/WishlistContext'
import fallbackProductImage from '../../assets/images/FortuneBasket.png'

const FortuneBasketCard = ({ item }) => {
  const imageUrl = getProductPrimaryImage(item) || fallbackProductImage
  const listPrice = item.price != null ? Number(item.price) : null
  const salePrice =
    item.discount_price != null ? Number(item.discount_price) : null
  const hasDiscount =
    salePrice != null && listPrice != null && salePrice < listPrice
  const displayPrice = hasDiscount ? salePrice : listPrice
  const productId = String(item.product_id ?? item.service_id ?? '')

  const { isWishlisted, toggleWishlist } = useWishlist()
  const { addToCart, isInCart } = useCart()
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' })
  const [busy, setBusy] = useState(false)

  const wishlisted = isWishlisted(productId)
  const inCart = isInCart(productId)

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity })
  }

  const handleWishlist = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      const result = await toggleWishlist(item)
      if (result?.reason === 'login' || result?.reason === 'loading') return
      if (!result?.ok) {
        showToast(result?.message || 'Could not update wishlist', 'error')
        return
      }
      showToast(
        result.removed ? 'Removed from wishlist' : 'Added to wishlist',
        'success'
      )
    } finally {
      setBusy(false)
    }
  }

  const handleCart = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      const result = await addToCart(item)
      if (result?.reason === 'login' || result?.reason === 'loading') return
      if (!result?.ok) {
        showToast(result?.message || 'Could not update cart', 'error')
        return
      }
      showToast(
        result.alreadyInCart
          ? 'Quantity updated in cart'
          : 'Added to cart',
        'success'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className="p-[14px] rounded-xl second-bg fortune-basket-card d-flex flex-column">
      <div className="fortune-basket-card__media">
        <img
          src={imageUrl}
          alt={item.product_name || 'Product'}
          className="fortune-basket-card__image"
          onError={(event) => {
            event.currentTarget.onerror = null
            event.currentTarget.src = fallbackProductImage
          }}
        />
        <button
          type="button"
          className={`fortune-basket-card__wishlist${
            wishlisted ? ' is-active' : ''
          }`}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          onClick={handleWishlist}
          disabled={busy}
        >
          <FiHeart
            fill={wishlisted ? 'currentColor' : 'none'}
            strokeWidth={wishlisted ? 0 : 2}
          />
        </button>
      </div>

      <div className="pt-3 pb-2 fortune-basket-card__body">
        <div className="fortune-basket-card__info">
          <h4
            className="text-lora font-semibold mb-2 fortune-basket-card__title"
            title={item.product_name}
          >
            {item.product_name}
          </h4>

          <div className="fortune-basket-card__price-row d-flex align-items-baseline gap-2 flex-wrap">
            {displayPrice != null && Number.isFinite(displayPrice) ? (
              <>
                <span className="fortune-basket-card__price">
                  ₹{formatProductPrice(displayPrice)}
                </span>
                {hasDiscount && (
                  <span className="fortune-basket-card__mrp">
                    ₹{formatProductPrice(listPrice)}
                  </span>
                )}
              </>
            ) : (
              <span className="fortune-basket-card__price">-</span>
            )}
          </div>
        </div>

        <button
          type="button"
          className={`fortune-basket-card__cart${inCart ? ' is-active' : ''}`}
          aria-label={inCart ? 'Update cart quantity' : 'Add to cart'}
          onClick={handleCart}
          disabled={busy}
        >
          <FiShoppingCart />
        </button>
      </div>

      <div className="fortune-basket-card__actions">
        <NavLink
          className="no-underline text-white first-btn text-center"
          to={`/shop/${item.product_id}`}
          state={item}
        >
          Order Now
        </NavLink>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </article>
  )
}

export default FortuneBasketCard
